import React, { useState } from 'react';
import { FootHealthAssessment, FOOT_REGIONS } from '../FootHealthMonitoring';
import { ShoppingBag, AlertCircle, Check, X, BarChart2 } from 'lucide-react';

interface FootwearAnalysisProps {
  assessment: FootHealthAssessment;
}

interface FootwearIssue {
  id: string;
  name: string;
  description: string;
  pressureSignature: {
    regionId: string;
    condition: 'high' | 'low' | 'normal';
    threshold: number;
  }[];
  recommendations: string[];
  detected: boolean;
}

const FootwearAnalysis: React.FC<FootwearAnalysisProps> = ({ assessment }) => {
  const [activeFoot, setActiveFoot] = useState<'left' | 'right'>('right'); // Start with right foot as it shows the problem in our mock data
  
  // Helper to evaluate if a pressure value meets a condition
  const evaluatePressureCondition = (value: number, condition: 'high' | 'low' | 'normal', threshold: number): boolean => {
    switch(condition) {
      case 'high':
        return value > threshold;
      case 'low':
        return value < threshold;
      case 'normal':
        return value >= threshold * 0.8 && value <= threshold * 1.2;
      default:
        return false;
    }
  };
  
  // Define common footwear issues and their pressure signatures
  const footwearIssues: FootwearIssue[] = [
    {
      id: 'narrow-shoes',
      name: 'Narrow Shoes',
      description: 'Shoes that are too narrow cause increased pressure on the lateral sides of the foot.',
      pressureSignature: [
        { regionId: 'lateral_forefoot', condition: 'high', threshold: 50 },
        { regionId: 'lateral_midfoot', condition: 'high', threshold: 40 }
      ],
      recommendations: [
        'Choose shoes with a wider toe box',
        'Consider shoes marked with "wide" width',
        'Look for shoes with a roomier forefoot area'
      ],
      detected: false
    },
    {
      id: 'poor-arch-support',
      name: 'Insufficient Arch Support',
      description: 'Shoes with inadequate arch support lead to improper weight distribution and increased heel pressure.',
      pressureSignature: [
        { regionId: 'heel', condition: 'high', threshold: 60 },
        { regionId: 'medial_midfoot', condition: 'low', threshold: 20 }
      ],
      recommendations: [
        'Choose shoes with built-in arch support',
        'Consider adding supportive insoles or orthotics',
        'Avoid flat shoes with minimal cushioning'
      ],
      detected: false
    },
    {
      id: 'inadequate-cushioning',
      name: 'Inadequate Cushioning',
      description: 'Insufficient cushioning in shoes leads to higher impact forces throughout the foot.',
      pressureSignature: [
        { regionId: 'heel', condition: 'high', threshold: 65 },
        { regionId: 'forefoot', condition: 'high', threshold: 55 }
      ],
      recommendations: [
        'Select shoes with adequate cushioning, especially in the heel',
        'Look for shoes with shock-absorbing midsoles',
        'Replace worn shoes that have lost their cushioning properties'
      ],
      detected: false
    },
    {
      id: 'improper-length',
      name: 'Improper Shoe Length',
      description: 'Shoes that are too short cause increased pressure on the toes and can lead to toe deformities.',
      pressureSignature: [
        { regionId: 'hallux', condition: 'high', threshold: 55 },
        { regionId: 'lesser_toes', condition: 'high', threshold: 50 }
      ],
      recommendations: [
        'Ensure 1/2 inch space between longest toe and end of shoe',
        'Size shoes in the afternoon when feet are naturally larger',
        'Remember that different brands may fit differently'
      ],
      detected: false
    }
  ];
  
  // Get foot data based on active selection
  const footData = activeFoot === 'left' ? assessment.leftFootData : assessment.rightFootData;
  
  // Analyze footwear issues based on pressure data
  const analyzedIssues = footwearIssues.map(issue => {
    // Check if the pressure signature matches
    const matchesSignature = issue.pressureSignature.every(sig => {
      const regionData = footData.regions[sig.regionId];
      if (!regionData) return false;
      
      return evaluatePressureCondition(regionData.averagePressure, sig.condition, sig.threshold);
    });
    
    return {
      ...issue,
      detected: matchesSignature
    };
  });
  
  // Prepare data for bar chart
  const regionPressureData = FOOT_REGIONS.map(region => {
    const regionData = footData.regions[region.id];
    const isAbnormal = regionData.averagePressure < region.normalPressureRange[0] || 
                     regionData.averagePressure > region.normalPressureRange[1];
    
    return {
      name: region.name,
      pressure: regionData.averagePressure,
      normalMin: region.normalPressureRange[0],
      normalMax: region.normalPressureRange[1],
      abnormal: isAbnormal
    };
  });
  
  // Calculate overall footwear score (0-100)
  const calculateFootwearScore = () => {
    // Start with perfect score and deduct for issues
    let score = 100;
    
    // Deduct for detected issues
    analyzedIssues.forEach(issue => {
      if (issue.detected) {
        score -= 20; // Deduct 20 points for each major issue
      }
    });
    
    // Deduct for regions outside normal range
    regionPressureData.forEach(region => {
      if (region.abnormal) {
        score -= 5; // Deduct 5 points for each abnormal region
      }
    });
    
    // Ensure score is within 0-100 range
    return Math.max(0, Math.min(100, score));
  };
  
  const footwearScore = calculateFootwearScore();
  
  // Generate footwear recommendation
  const getFootwearRecommendation = () => {
    const detectedIssues = analyzedIssues.filter(issue => issue.detected);
    
    if (detectedIssues.length === 0) {
      return "Your current footwear appears to provide good support for your feet. Continue to monitor for any changes in comfort or foot pressure patterns.";
    }
    
    // Focus on the most significant issue
    const primaryIssue = detectedIssues[0];
    
    return `Your foot pressure patterns suggest ${primaryIssue.name.toLowerCase()}. ${primaryIssue.description} Consider changing to footwear that provides better support for your specific needs.`;
  };
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium">Footwear Analysis</h3>
        <div className="flex items-center space-x-1 text-sm">
          <button
            onClick={() => setActiveFoot('left')}
            className={`px-3 py-1 rounded-l-md ${
              activeFoot === 'left' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Left Foot
          </button>
          <button
            onClick={() => setActiveFoot('right')}
            className={`px-3 py-1 rounded-r-md ${
              activeFoot === 'right' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Right Foot
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Footwear score card */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <ShoppingBag className="text-purple-500 mr-2" size={20} />
              <h4 className="font-medium">Footwear Compatibility Score</h4>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              footwearScore >= 80 ? 'bg-green-100 text-green-800' :
              footwearScore >= 60 ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {footwearScore}/100
            </div>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
            <div 
              className={`h-4 rounded-full ${
                footwearScore >= 80 ? 'bg-green-500' :
                footwearScore >= 60 ? 'bg-yellow-500' :
                'bg-red-500'
              }`}
              style={{ width: `${footwearScore}%` }}
            ></div>
          </div>
          
          <p className="text-sm text-gray-700 mb-4">
            {getFootwearRecommendation()}
          </p>
          
          {analyzedIssues.some(issue => issue.detected) && (
            <div className="mt-3 p-3 bg-yellow-50 text-yellow-700 rounded-lg border border-yellow-200 flex items-start">
              <AlertCircle size={16} className="mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-sm">
                Your current footwear may be contributing to abnormal pressure distribution.
                Consider consulting with a footwear specialist for proper fitting.
              </span>
            </div>
          )}
        </div>
        
        {/* Pressure distribution chart */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-3">Regional Pressure Distribution</h4>
          
          <div className="space-y-4">
            {regionPressureData.map((region, index) => (
              <div key={index} className="flex flex-col">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm">{region.name}</span>
                  <span className={`text-sm font-medium ${region.abnormal ? 'text-red-600' : 'text-gray-700'}`}>
                    {region.pressure.toFixed(1)}
                  </span>
                </div>
                
                <div className="relative h-6 w-full bg-gray-200 rounded">
                  {/* Normal range indicator */}
                  <div 
                    className="absolute top-0 bottom-0 bg-green-100"
                    style={{ 
                      left: `${(region.normalMin / 100) * 100}%`,
                      width: `${((region.normalMax - region.normalMin) / 100) * 100}%`
                    }}
                  ></div>
                  
                  {/* Current value bar */}
                  <div 
                    className={`absolute top-0 bottom-0 ${region.abnormal ? 'bg-red-500' : 'bg-blue-500'}`}
                    style={{ 
                      width: `${(region.pressure / 100) * 100}%` 
                    }}
                  ></div>
                </div>
                
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-gray-500">0</span>
                  <span className="text-gray-500">
                    Normal: {region.normalMin} - {region.normalMax}
                  </span>
                  <span className="text-gray-500">100</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Detected footwear issues */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="font-medium mb-3">Footwear Issues Analysis</h4>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {analyzedIssues.map((issue, index) => (
            <div 
              key={index}
              className={`p-4 rounded-lg border ${
                issue.detected ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium">{issue.name}</h5>
                <div className={`h-6 w-6 rounded-full flex items-center justify-center ${
                  issue.detected 
                    ? 'bg-red-100 text-red-600' 
                    : 'bg-green-100 text-green-600'
                }`}>
                  {issue.detected ? <X size={16} /> : <Check size={16} />}
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-3">{issue.description}</p>
              
              {issue.detected && (
                <div className="mt-2">
                  <h6 className="text-sm font-medium mb-1">Recommendations:</h6>
                  <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                    {issue.recommendations.map((rec, recIndex) => (
                      <li key={recIndex}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Footwear selection guidance */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium mb-3">Footwear Selection Guidance</h4>
        
        <p className="text-sm text-gray-700 mb-4">
          Based on your foot pressure patterns, look for shoes with the following features:
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-3 rounded-lg bg-white border border-gray-200">
            <h5 className="font-medium mb-2">Key Features to Look For</h5>
            <ul className="text-sm text-gray-700 space-y-2">
              {assessment.overallAssessment.plantarFasciitisRisk > 30 && (
                <li className="flex items-center">
                  <div className="h-4 w-4 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-2">
                    <Check size={12} />
                  </div>
                  Strong arch support
                </li>
              )}
              {footData.regions.heel.averagePressure > 60 && (
                <li className="flex items-center">
                  <div className="h-4 w-4 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-2">
                    <Check size={12} />
                  </div>
                  Extra heel cushioning
                </li>
              )}
              {analyzedIssues.find(i => i.id === 'narrow-shoes')?.detected && (
                <li className="flex items-center">
                  <div className="h-4 w-4 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-2">
                    <Check size={12} />
                  </div>
                  Wide toe box
                </li>
              )}
              <li className="flex items-center">
                <div className="h-4 w-4 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-2">
                  <Check size={12} />
                </div>
                Proper size and fit
              </li>
              <li className="flex items-center">
                <div className="h-4 w-4 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-2">
                  <Check size={12} />
                </div>
                Good shock absorption
              </li>
            </ul>
          </div>
          
          <div className="p-3 rounded-lg bg-white border border-gray-200">
            <h5 className="font-medium mb-2">Features to Avoid</h5>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center">
                <div className="h-4 w-4 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">
                  <X size={12} />
                </div>
                Completely flat soles
              </li>
              <li className="flex items-center">
                <div className="h-4 w-4 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">
                  <X size={12} />
                </div>
                Worn-out cushioning
              </li>
              {analyzedIssues.find(i => i.id === 'narrow-shoes')?.detected && (
                <li className="flex items-center">
                  <div className="h-4 w-4 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">
                    <X size={12} />
                  </div>
                  Narrow or pointed toe boxes
                </li>
              )}
              {footData.regions.heel.averagePressure > 60 && (
                <li className="flex items-center">
                  <div className="h-4 w-4 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">
                    <X size={12} />
                  </div>
                  Hard, inflexible soles
                </li>
              )}
              <li className="flex items-center">
                <div className="h-4 w-4 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">
                  <X size={12} />
                </div>
                High heels (for daily wear)
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FootwearAnalysis; 